<script setup>
import { computed, defineProps, ref, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";
import {
  useGetEntitySupplement,
  useGetHostInstitutionLanguages,
} from "@/components/program-manager/sessions/composable.js";
import CloudinaryVideo from "@/components/shared/CloudinaryVideo/index.vue";
import { CONTACT_HOURS, ERROR_TIMEOUT } from "@/constants.js";
import { firstVideoMedia, imageTransformation, isImageMedia } from "@/util/cloudinary";
import { toNonEmptyTrimmedString } from "@/util/string";

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_UI_GOOGLE_PLACES_API_KEY;
const NOT_AVAILABLE = "Not available";

const toast = useToast();

const props = defineProps({
  hostInstitution: {
    type: Object,
    required: true,
  },
});

const { hostInstitution } = toRefs(props);
const video = ref(undefined);
const numberOfStudents = ref(0);
const mediaName = computed(() => toNonEmptyTrimmedString(hostInstitution.value?.name) ?? "Host institution");
// biome-ignore lint/correctness/noUnusedVariables: bound in the template, which Biome does not see
const videoLabel = computed(() => `${mediaName.value} program video`);

const { execute: executeFetchEntitySupplementOptions, state: entitySupplementData } = useGetEntitySupplement(
  undefined,
  false,
);
const { execute: executeFetchHostInstitutionLanguagesOptions, state: languageData } = useGetHostInstitutionLanguages(
  undefined,
  false,
);

watch(
  hostInstitution,
  async (newHostInstitution) => {
    if (newHostInstitution?.id) {
      video.value = undefined;
      try {
        await executeFetchHostInstitutionLanguagesOptions(0, newHostInstitution.id);
        await executeFetchEntitySupplementOptions(0, newHostInstitution.id);
      } catch (exception) {
        toast.open({
          message: "Was not able to load Institution data.",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    }
  },
  {
    immediate: true,
  },
);

const images = computed(
  () =>
    entitySupplementData.value?.items?.supporting_media
      ?.filter((item) => isImageMedia(item))
      ?.map((img) => imageTransformation({ path: img, compress: true })) || [],
);

watch(
  entitySupplementData,
  (newEntitySupplementData) => {
    numberOfStudents.value = newEntitySupplementData.items?.total_number_of_students ?? 0;

    video.value = firstVideoMedia(newEntitySupplementData.items?.supporting_media);
  },
  {
    immediate: true,
  },
);

const languageShowCount = computed(() =>
  languageData.value?.count > languageData.value?.end
    ? ` + ${languageData.value?.count - languageData.value?.end} more languages`
    : "",
);

const languages = computed(() => {
  return languageData.value?.count > 0
    ? `${languageData.value?.items?.map(({ value }) => value).join(", ")}${languageShowCount.value}`
    : undefined;
});

const address = computed(() => {
  if (!hostInstitution.value) return NOT_AVAILABLE;

  let addr = hostInstitution.value?.shipping_address_city ? `${hostInstitution.value.shipping_address_city}, ` : "";
  addr += hostInstitution.value?.shipping_address_line_1 ? `${hostInstitution.value.shipping_address_line_1}, ` : "";
  addr += hostInstitution.value?.shipping_address_postal_code
    ? `${hostInstitution.value.shipping_address_postal_code}, `
    : "";
  addr += hostInstitution.value?.shipping_address_country ? `${hostInstitution.value.shipping_address_country}, ` : "";
  addr += hostInstitution.value?.name ? `${hostInstitution.value.name}, ` : "";
  return addr.replace(/\r?\n/g, " ").replace(/,*$/, "").trim() || NOT_AVAILABLE;
});

const getMap = computed(() => {
  if (address.value !== NOT_AVAILABLE) {
    const googleApiKey = GOOGLE_PLACES_API_KEY;
    const zoom = 15;
    return `https://maps.googleapis.com/maps/api/staticmap?key=${googleApiKey}&center=${address.value}&zoom=${zoom}&format=png&maptype=roadmap&style=feature:road.arterial%7Cvisibility:off&style=feature:road.highway%7Celement:labels%7Cvisibility:off&style=feature:road.local%7Cvisibility:off&size=1200x360&scale=2&markers=color:red%7C${address.value}`;
  }
  return undefined;
});

const creditType = computed(() => {
  const creditUnitName = hostInstitution.value?.credit_unit?.name;
  return creditUnitName && CONTACT_HOURS.includes(creditUnitName.trim())
    ? "Contact Hours"
    : creditUnitName || "Not available";
});

const aboutCampus = computed(() =>
  (entitySupplementData.value?.items?.tags?.map((item) => item?.name)?.filter((item) => item) || []).join(", "),
);

const numberOfStudentsFormatted = computed(() => {
  // Adds comma to a number (1500 => 1,500)
  return Number(numberOfStudents.value).toLocaleString("en-US");
});
</script>
<template>
  <div
    v-if="hostInstitution"
    class="pt-12 pb-12 md:pt-20 px-4 md:px-8 text-indigo-base text-indigo-base relative h-fit"
  >
    <div>
      <div>
        <div class="pb-8 font-bold text-4xl text-indigo-base">
          <div class="self-center text-2xl">Facts About</div>
          <h2 class="self-center">
            {{ hostInstitution?.name || "" }}
          </h2>
        </div>
        <!-- v-if, not v-show: the player measures its element on creation. -->
        <div v-if="video" class="w-full h-fit pb-8 mx-auto">
          <CloudinaryVideo :src="video" :label="videoLabel" :poster="images?.[0]" />
        </div>
        <div
          v-else-if="images?.length"
          class="w-full h-fit pb-8 mx-auto"
        >
          <img
            v-if="images?.[0]"
            :src="images?.[0]"
            loading="lazy"
            :alt="mediaName"
            class="w-full lg:max-h-[35rem] xl:max-h-[45rem] ml-auto"
          />
        </div>
        <div>
          <div class="flex flex-col" :class="{ 'md:flex-row-reverse': getMap }">
            <div class="pb-8 md:pb-1 w-full">
              <div v-if="hostInstitution?.accreditation" class="mb-6">
                <span class="font-bold">Accreditation: </span>
                <span>{{
                  hostInstitution?.accreditation?.join(", ") || "Not available"
                }}</span>
              </div>
              <div
                v-if="
                  entitySupplementData?.items?.total_number_of_students &&
                  Number(
                    entitySupplementData?.items?.total_number_of_students
                  ) > 0
                "
                class="mb-6"
              >
                <span class="font-bold">Total number of students: </span>
                <span data-testid="total-number-of-students">
                  {{ numberOfStudentsFormatted }}
                </span>
              </div>
              <div
                v-if="
                  entitySupplementData?.items
                    ?.percentage_international_students &&
                  Number(
                    entitySupplementData?.items
                      ?.percentage_international_students
                  ) > 0
                "
                class="mb-6"
              >
                <span class="font-bold">
                  Percentage of international students:
                </span>
                <span>
                  {{
                    entitySupplementData?.items
                      ?.percentage_international_students
                  }}
                </span>
              </div>
              <div v-if="aboutCampus" class="mb-6">
                <span class="font-bold"
                  >About the campus and surrounding neighborhood:
                </span>
                <span>
                  {{ aboutCampus || "Not available" }}
                </span>
              </div>
              <div v-if="hostInstitution?.credit_unit" class="mb-6">
                <span class="font-bold">Credit Type: </span>
                <span data-testid="credit-type">
                  {{ creditType }}
                </span>
              </div>
            </div>
            <div v-if="getMap" class="pb-8 md:pb-1 md:pr-8 md:max-w-lg">
              <img
                id="host-institution-map"
                loading="lazy"
                :alt="address"
                :src="getMap"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="my-1 md:mt-20 border-b-3 border-gray-200" />
  </div>
</template>
